import React, { useEffect, useState, useRef } from 'react';

/**
 * ExplosionEffect component - renders an explosion animation at a specific position
 * Used for strategem 114 (Tactical Loadout), 115 (Stinger Unit Alpha), and 225 when requested
 * @param {Object} props
 * @param {number} props.x - X position in pixels (center of explosion)
 * @param {number} props.y - Y position in pixels (center of explosion)
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {string} props.animationKey - Unique key for this animation instance
 * @param {string} props.color - Color scheme: 'orange' (default), 'purple', or 'black'
 */
export const ExplosionEffect = ({ x, y, onComplete, animationKey, color = 'orange' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!animationKey) return;

    console.log('💥 ExplosionEffect: Starting animation', { animationKey, x, y });
    setIsVisible(true);
    startTimeRef.current = Date.now();

    const DURATION = 600; // 600ms animation duration

    const animate = () => {
      if (!startTimeRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min(elapsed / DURATION, 1);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        console.log('✅ ExplosionEffect: Animation complete', { animationKey });
        setIsVisible(false);
        if (onComplete) {
          onComplete();
        }
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [animationKey, x, y, onComplete]);

  if (!isVisible) {
    return null;
  }

  // Color scheme based on prop
  const isPurple = color === 'purple';
  const isBlack = color === 'black';
  const mainColor1 = isBlack ? 'rgba(0, 0, 0, 0.9)' : isPurple ? 'rgba(150, 50, 200, 0.8)' : 'rgba(255, 100, 0, 0.8)';
  const mainColor2 = isBlack ? 'rgba(30, 30, 30, 0.7)' : isPurple ? 'rgba(200, 100, 255, 0.6)' : 'rgba(255, 200, 0, 0.6)';
  const mainColor3 = isBlack ? 'rgba(60, 60, 60, 0.5)' : isPurple ? 'rgba(255, 200, 255, 0.4)' : 'rgba(255, 255, 255, 0.4)';
  const ringColor = isBlack ? 'rgba(20, 20, 20, 0.8)' : isPurple ? 'rgba(200, 100, 255, 0.6)' : 'rgba(255, 200, 0, 0.6)';
  const ringShadow = isBlack ? 'rgba(0, 0, 0, 0.6)' : isPurple ? 'rgba(200, 100, 255, 0.4)' : 'rgba(255, 200, 0, 0.4)';
  const insetColor = isBlack ? 'rgba(10, 10, 10, 0.7)' : isPurple ? 'rgba(200, 100, 255, 0.5)' : 'rgba(255, 200, 0, 0.5)';

  return (
    <div
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, -50%)',
        width: '300px',
        height: '300px',
        pointerEvents: 'none',
        zIndex: 10000,
        opacity: isVisible ? 1 : 0,
      }}
      data-animation-key={animationKey}
    >
      {/* Main explosion circle */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${mainColor1} 0%, ${mainColor2} 30%, ${mainColor3} 50%, transparent 100%)`,
          boxShadow: `
            0 0 30px ${mainColor1},
            0 0 60px ${mainColor2},
            0 0 90px ${mainColor3},
            inset 0 0 40px ${insetColor}
          `,
          animation: 'explosion-expand 0.6s ease-out forwards',
        }}
      />
      {/* Outer ring */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '120%',
          height: '120%',
          borderRadius: '50%',
          border: `3px solid ${ringColor}`,
          boxShadow: `0 0 40px ${ringShadow}`,
          animation: 'explosion-ring 0.6s ease-out forwards',
        }}
      />
      {/* Particles */}
      {[...Array(8)].map((_, i) => {
        let particleR, particleG, particleB;
        if (isBlack) {
          particleR = 10 + i * 2;
          particleG = 10 + i * 2;
          particleB = 10 + i * 2;
        } else if (isPurple) {
          particleR = 100 + i * 10;
          particleG = 50 + i * 15;
          particleB = 200 + i * 5;
        } else {
          particleR = 255;
          particleG = 150 + i * 10;
          particleB = i * 20;
        }
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: `rgba(${particleR}, ${particleG}, ${particleB}, 0.8)`,
              boxShadow: `0 0 10px rgba(${particleR}, ${particleG}, ${particleB}, 0.6)`,
              animation: `explosion-particle-${i} 0.6s ease-out forwards`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes explosion-expand {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.9;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
        }
        @keyframes explosion-ring {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0;
          }
        }
        ${[...Array(8)].map((_, i) => {
          const angle = (i * 45) * (Math.PI / 180);
          const distance = 100;
          const endX = Math.cos(angle) * distance;
          const endY = Math.sin(angle) * distance;
          return `
            @keyframes explosion-particle-${i} {
              0% {
                transform: translate(-50%, -50%) translate(0, 0) scale(1);
                opacity: 1;
              }
              100% {
                transform: translate(-50%, -50%) translate(${endX}px, ${endY}px) scale(0);
                opacity: 0;
              }
            }
          `;
        }).join('')}
      `}</style>
    </div>
  );
};

export default ExplosionEffect;
